import { type LocaleDefinition } from '@solinkcorp/locale';
import {
  ALL_LOCATIONS,
  type GenerateUrlParams,
  generateVideoDiscoveryURL,
  SEARCH_OPERATOR,
  type SearchOperator,
} from '@solinkcorp/vd-common';
import { type FieldValue } from 'components/EventsPage/renderers/types';
import LocaleUtil from 'utils/LocaleUtil';
import { ROUTE_EVENTS, SOURCE_LINK } from 'vd-constants';
import XRegExp from 'xregexp';

const regexNonUnicodeWordEnding = XRegExp('[^\\pL\\pN]+$');
const regexUnicodeWord = XRegExp('^[\\pL\\pN]+$');

export interface EventSearchCapsule {
  name: string;
  value: FieldValue;
  i18n: string;
}

export interface EventSearchContext {
  origin: string;
  eventFilters: { transaction: boolean; motion: boolean; bookmark: boolean };
  filterDate: number;
  locationIds?: readonly string[];
  tenantId: string;
  devicesBySiteId?: Record<string, Array<{ id: string }> | undefined>;
  searchString?: string;
  searchCapsules?: readonly string[];
  searchOperator?: SearchOperator;
  selectedFilterId?: string;
  selectedReportName?: string;
  locale: {
    event: Pick<LocaleDefinition['event'], 'searchReplace' | 'searchAppend'>;
  };
}

export interface EventSearchUrls {
  replaceSearchUrl: string;
  replaceSearchText: string;
  appendSearchUrl: string;
  appendSearchText: string;
}

/** Escape a free-text selection the way the legacy card search does. */
export function prepareEventSearchText(raw: string): string | undefined {
  const str = raw.trim();
  if (!str) return undefined;

  let replacedStr = str
    .replace(/^"(.*)([^\\])"$/, '$1$2')
    .replace(/[><]/g, '')
    .replace(/(\+|-|=|&{2}|\|{2}|!|\(|\)|\{|\}|\[|\]|\^|"|~|\*|\?|:|\\|\/)/g, '\\$1')
    .replace(regexNonUnicodeWordEnding, '');

  if (regexUnicodeWord.test(replacedStr)) {
    replacedStr = `${replacedStr}*`;
  } else {
    replacedStr = `"${replacedStr}"`;
  }
  return replacedStr;
}

/**
 * Build the replace and append Events search URLs for a text selection or a
 * details capsule. Returns undefined when there is nothing to search.
 */
export function buildEventSearchUrls(
  input: { text?: string; capsule?: EventSearchCapsule },
  ctx: EventSearchContext,
  options?: { markup?: boolean },
): EventSearchUrls | undefined {
  const replacedStr = input.text ? prepareEventSearchText(input.text) : undefined;
  const capsule = input.capsule;
  if (!replacedStr && !capsule) return undefined;

  const searchText = capsule ? `${capsule.i18n} = ${capsule.value}` : (input.text ?? '').trim();
  const newSearchCapsule = capsule ? `${capsule.name}&nbsp;=&nbsp;${capsule.value}` : undefined;

  const {
    eventFilters,
    filterDate,
    locationIds,
    locale,
    searchString,
    searchCapsules,
    searchOperator,
    selectedFilterId,
    selectedReportName,
    tenantId,
    devicesBySiteId,
    origin,
  } = ctx;

  const replaceSearchObj: GenerateUrlParams = {
    origin,
    route: ROUTE_EVENTS,
    eventFilter: { transaction: true, motion: true, bookmark: true },
    starred: false,
    date: new Date(),
    tenantId,
    locationId: ALL_LOCATIONS,
    source: SOURCE_LINK,
    searchOperator,
  };

  const appendSearchObj: GenerateUrlParams = {
    origin,
    route: ROUTE_EVENTS,
    eventFilter: {
      transaction: eventFilters.transaction,
      motion: eventFilters.motion,
      bookmark: eventFilters.bookmark,
    },
    date: new Date(filterDate),
    tenantId,
    siteIds: [...(locationIds ?? [])],
    locationId: devicesBySiteId?.[locationIds?.[0] ?? '']?.[0]?.id,
    reportId: selectedFilterId,
    source: SOURCE_LINK,
    searchOperator,
  };

  if (replacedStr) {
    replaceSearchObj.searchString = replacedStr;
    appendSearchObj.searchString = searchString
      ? `${searchString} ${searchOperator || SEARCH_OPERATOR.AND} ${replacedStr}`
      : replacedStr;
    if (searchCapsules) appendSearchObj.capsules = searchCapsules as string[];
  } else if (newSearchCapsule) {
    replaceSearchObj.capsules = [newSearchCapsule];
    if (searchString) appendSearchObj.searchString = searchString;
    appendSearchObj.capsules = searchCapsules
      ? [...searchCapsules, newSearchCapsule]
      : [newSearchCapsule];
  }

  const _searchText = searchText.length > 50 ? `${searchText.substring(0, 50)}...` : searchText;
  const quote = (value: string) => `"${value}"`;
  const escapeHtml = (value: string) =>
    value
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  const replacements =
    options?.markup === false
      ? { term: quote(_searchText), report: quote(selectedReportName ?? '') }
      : {
          term: `<span>${quote(escapeHtml(_searchText))}</span>`,
          report: `<span>${quote(escapeHtml(selectedReportName ?? ''))}</span>`,
        };

  return {
    replaceSearchUrl: generateVideoDiscoveryURL(replaceSearchObj),
    replaceSearchText: LocaleUtil.getLocalizedStr({
      string: locale.event.searchReplace,
      replacements,
    }),
    appendSearchUrl: generateVideoDiscoveryURL(appendSearchObj),
    appendSearchText: LocaleUtil.getLocalizedStr({
      string: locale.event.searchAppend,
      replacements,
    }),
  };
}
